import type { ComboboxRootChangeEventDetails } from "@base-ui/react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Clock, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from "@/components/ui/combobox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/utils/customHook.util";
import { AiCapturedBadge } from "../ai-capture/AiCapturedBadge";
import { type LocationSuggestion, useLocationSearch } from "./LocationSearchContext";

export type LocationOption = {
    city: string;
    country: string;
    countryCode: string;
    /** IANA time zone identifier, used to show the city's current local time. */
    timezone?: string;
};

const SEARCH_DEBOUNCE_MS = 250;
const EMPTY_SUGGESTIONS: LocationSuggestion[] = [];

/** Current time in `timezone`, refreshed every minute. */
function useClock(timezone: string | undefined): string | undefined {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        if (!timezone) return;
        const id = setInterval(() => setNow(new Date()), 60_000);
        return () => clearInterval(id);
    }, [timezone]);

    if (!timezone) return undefined;

    return new Intl.DateTimeFormat("en-GB", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
    }).format(now);
}

/** Replaces the pin icon: hover reveals the city's current local time. */
function LocalTimeIcon({ timezone }: { timezone: string }) {
    const time = useClock(timezone);

    return (
        <Tooltip>
            <TooltipTrigger
                aria-label={`Current local time ${time}`}
                render={<span className="inline-flex items-center" />}
            >
                <Clock className="size-4 text-muted-foreground" aria-hidden />
            </TooltipTrigger>
            <TooltipContent side="top" align="center" sideOffset={8}>
                Current local time: {time}
            </TooltipContent>
        </Tooltip>
    );
}

export type LocationComboboxProps = {
    id?: string;
    value?: string;
    defaultValue?: string;
    onValueChange?: (value: string) => void;
    onPlaceChange?: (place: LocationOption) => void;
    placeholder?: string;
    aiCaptured?: boolean;
    /**
     * Once a city with a known time zone is selected, swap the pin icon for a
     * clock that reveals the city's current local time on hover.
     */
    withLocalTime?: boolean;
    /** Time zone of the pre-filled `value` / `defaultValue` city. */
    defaultTimezone?: string;
};

export function LocationCombobox({
    id,
    value,
    defaultValue,
    onValueChange,
    onPlaceChange,
    placeholder = "Search by city",
    aiCaptured = false,
    withLocalTime = false,
    defaultTimezone,
}: LocationComboboxProps) {
    const { searchLocation, resolveLocation } = useLocationSearch();
    const [searchTerm, setSearchTerm] = useState("");
    const [timezone, setTimezone] = useState(defaultTimezone);
    const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS).trim();

    const { data: fetchedSuggestions = EMPTY_SUGGESTIONS } = useQuery({
        queryKey: ["location-search", debouncedSearchTerm],
        queryFn: () => searchLocation(debouncedSearchTerm),
        enabled: debouncedSearchTerm.length > 0,
        placeholderData: keepPreviousData,
    });
    const suggestions =
        debouncedSearchTerm.length > 0 ? fetchedSuggestions : EMPTY_SUGGESTIONS;
    const items = suggestions.map((suggestion) => suggestion.placeId);

    function handleInputChange(
        next: string,
        eventDetails: ComboboxRootChangeEventDetails,
    ) {
        if (eventDetails.reason === "input-change") {
            setSearchTerm(next);
        }
    }

    async function handleSelect(placeId: string) {
        const suggestion = suggestions.find((item) => item.placeId === placeId);
        const label = suggestion?.label ?? placeId;
        onValueChange?.(label);

        const place = await resolveLocation(placeId);
        setTimezone(place?.timezone);
        onPlaceChange?.(place ?? { city: label, country: "", countryCode: "" });
    }

    return (
        <Combobox
            autoHighlight
            filter={null}
            items={items}
            defaultInputValue={value ?? defaultValue}
            itemToStringLabel={(placeId: string) => labelFor(suggestions, placeId)}
            onInputValueChange={handleInputChange}
            onValueChange={(next) => {
                if (typeof next === "string") void handleSelect(next);
            }}
        >
            <ComboboxInput
                id={id}
                placeholder={placeholder}
                showTrigger={!aiCaptured}
                className={cn("w-full", aiCaptured && "!border-blue-200 !bg-blue-50")}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                    }
                }}
            >
                {aiCaptured ? (
                    <AiCapturedBadge inline />
                ) : withLocalTime && timezone ? (
                    <LocalTimeIcon timezone={timezone} />
                ) : (
                    <MapPin className="size-4 text-muted-foreground" />
                )}
            </ComboboxInput>
            <ComboboxContent>
                <ComboboxEmpty>No cities match</ComboboxEmpty>
                <ComboboxList>
                    {(placeId: string) => (
                        <ComboboxItem key={placeId} value={placeId}>
                            {labelFor(suggestions, placeId)}
                        </ComboboxItem>
                    )}
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}

function labelFor(suggestions: LocationSuggestion[], placeId: string): string {
    return suggestions.find((item) => item.placeId === placeId)?.label ?? placeId;
}
